import React, { useState } from "react";
import styled, { css } from "styled-components";

const EmailSender = () => {
  const [senderName, setSenderName] = useState("");
  const [email, setEmail] = useState("");
  const [telefonePessoal, setTelefonePessoal] = useState("");
  const [telefoneRecado, setTelefoneRecado] = useState("");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [successSend, setSuccessSend] = useState("");
  const MAX_FILE_SIZE_MB = 2; // 2MB

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFormError("O arquivo PDF deve ter no máximo 2MB.");
      setFile(null);
    } else {
      setFormError("");
      setFile(selectedFile);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        const bytes = new Uint8Array(reader.result);
        let binary = "";
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(btoa(binary)); // Base64 only, no Data URI
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError("");

    if (
      !senderName.trim() ||
      !email.trim() ||
      !telefonePessoal.trim() ||
      !telefoneRecado.trim() ||
      !message.trim() ||
      !file
    ) {
      setFormError(
        "Por favor, preencha todos os campos obrigatórios e anexe o currículo."
      );
      setIsLoading(false);
      return;
    }

    try {
      const base64Data = await convertToBase64(file);
      const today = new Date().toLocaleDateString("pt-BR");

      const payload = {
        senderName,
        email,
        telefonePessoal,
        telefoneRecado,
        message,
        base64Data,
        date: today,
      };

      const response = await fetch(
        "https://7f5ddxkgnh.execute-api.sa-east-1.amazonaws.com/prod/rh-form",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      console.log("Resposta do servidor:", result);

      if (response.ok) {
        setSuccessSend("Candidatura enviada com sucesso!");
        // Clear all form fields
        setSenderName("");
        setEmail("");
        setTelefonePessoal("");
        setTelefoneRecado("");
        setMessage("");
        setFile(null);

        // Also clear file input manually if needed
        document.querySelector('input[type="file"]').value = "";
      } else {
        setFormError(result?.error || "Erro desconhecido.");
      }
    } catch (err) {
      console.error("Erro:", err);
      setFormError(
        "Ocorreu um erro ao enviar sua inscrição. Tente novamente mais tarde."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledFormContainer>
      <FormHeading>Formulário de Candidatura</FormHeading>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Nome Completo*</Label>
          <Input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder="Digite seu nome completo"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>E-mail*</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="seu.email@exemplo.com"
            required
          />
        </FormGroup>

        <FormRow>
          <FormGroup>
            <Label>Telefone Pessoal*</Label>
            <Input
              type="tel"
              value={telefonePessoal}
              onChange={(e) => setTelefonePessoal(e.target.value)}
              placeholder="45123456789"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Telefone para Recado*</Label>
            <Input
              type="tel"
              value={telefoneRecado}
              onChange={(e) => setTelefoneRecado(e.target.value)}
              placeholder="45123456789"
            />
          </FormGroup>
        </FormRow>

        <FormGroup>
          <Label>Mensagem/Carta de Apresentação*</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Escreva sua mensagem ou carta de apresentação..."
            rows="5"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>Anexar Currículo (PDF)*</Label>
          <FileInput
            accept="application/pdf"
            onChange={handleFileChange}
            required
          />
        </FormGroup>

        {formError && <p style={{ color: "red" }}>{formError}</p>}
        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? "Enviando..." : "Enviar"}
        </SubmitButton>
      </form>
      {successSend && <p style={{ color: "green" }}>{successSend}</p>}
    </StyledFormContainer>
  );
};

export default EmailSender;

const BaseInputStyles = css`
  width: 100%;
  padding: var(--spacing-medium) var(--spacing-large);
  border: 2px solid var(--color-accent);
  border-radius: 8px;
  font-size: var(--font-size-lg);
  font-family: var(--font-family-text);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: rgba(255, 255, 255, 0.9);
  color: var(--color-text-alt);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:focus {
    outline: none;
    border-color: var(--color-secondary-2);
    box-shadow: 0 0 0 4px rgba(255, 94, 20, 0.15),
      0 4px 12px rgba(0, 0, 0, 0.08);
    background: var(--color-primary);
  }

  &::placeholder {
    color: var(--color-tertiary);
    opacity: 0.6;
    transition: opacity 0.3s ease;
  }

  &:hover::placeholder {
    opacity: 0.8;
  }
`;

export const StyledFormContainer = styled.div`
  max-width: 880px;
  margin: var(--spacing-large) auto;
  padding: var(--spacing-large);

  border-radius: 16px;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transform: translateY(0);
  transition: transform 0.3s ease;
`;

export const FormHeading = styled.h2`
  color: var(--color-text-alt);
  margin-bottom: var(--spacing-large);
  font-family: var(--font-family-primary);
  font-weight: var(--font-weight-primary-medium);
  font-size: var(--font-size-xxl);
  position: relative;
  padding-bottom: var(--spacing-medium);

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: var(--color-secondary-2);
    border-radius: 2px;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: var(--spacing-large);
  position: relative;

  &:hover label {
    transform: translateX(4px);
  }
`;

export const FormRow = styled.div`
  display: grid;
  gap: var(--spacing-medium);
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: var(--spacing-extrasmall);
  font-weight: var(--font-weight-secondary-medium);
  color: var(--color-text-alt);
  font-family: var(--font-family-text);
  font-size: var(--font-size-sm);
  transition: transform 0.3s ease;
  position: relative;
  padding-left: var(--spacing-small);

  &::before {
    content: "•";
    color: var(--color-secondary-2);
    position: absolute;
    left: 0;
    top: -1px;
  }
`;

export const Input = styled.input`
  ${BaseInputStyles}
  height: 56px;
  padding-left: var(--spacing-large);
`;

export const Textarea = styled.textarea`
  ${BaseInputStyles}
  min-height: 140px;
  resize: vertical;
  padding: var(--spacing-medium) var(--spacing-large);
  line-height: 1.6;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-secondary-2);
    border-radius: 4px;
  }
`;

export const FileInput = styled.input.attrs({ type: "file" })`
  ${BaseInputStyles}
  padding: var(--spacing-extrasmall);
  cursor: pointer;
  position: relative;
  overflow: hidden;
`;

export const SubmitButton = styled.button`
  background: linear-gradient(
    135deg,
    var(--color-secondary-2) 0%,
    var(--color-secondary-2-dark) 100%
  );
  color: var(--color-text);
  padding: var(--spacing-medium) var(--spacing-large);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--font-size-lg);
  font-family: var(--font-family-accent);
  font-weight: var(--font-weight-accent-medium);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  width: 100%;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(4px);

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 94, 20, 0.3),
      0 4px 8px rgba(255, 94, 20, 0.15);

    &::after {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: var(--color-tertiary);
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const FormError = styled.div`
  color: var(--color-text);
  margin-top: var(--spacing-small);
  background: linear-gradient(
    90deg,
    var(--color-secondary-2),
    var(--color-secondary-2-dark)
  );
  padding: var(--spacing-medium);
  border-radius: 8px;
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-small);
  position: relative;
  overflow: hidden;
`;
